import { useState } from "react";
import Image from "next/image";

import { VatLine } from "./VatLine";
import { DiscountLine } from "./DiscountLine";
import { ArticleDetails } from "./ArticleDetails";

import { ExpandableButton, Button } from "@/components/common";

import {
  getArticleDisplay,
  translate,
  formatCurrency,
  getArticleButtonText,
} from "@/utils/formatters";
import { getButtonText } from "@/utils/extendedViewHelpers";

import { Article, Receipt, ExtendedViewItem } from "@/types/receipt";
import { useExtendedWidgetVisibility } from "@/hooks/useExtendedWidgetVisibility";
import { PopupArticle } from "@/constants/popup";

import styles from "./ArticleSection.module.css";

interface ArticleSectionProps {
  index: number;
  article: Article;
  lang: string;
  receipt: Receipt;
  openPopup: (
    type: string,
    index?: number,
    article?: PopupArticle
  ) => void;
  extended_view_items?: ExtendedViewItem[];
  extended_view_images?: { [key: string]: string };
  // The following props are passed through to popup components via prop drilling.
  // They're required for API calls and conditional data sharing in ExtendedReceiptViewPopup,
  // ReturnPopup, and  ServiceViewPopup components when users click action buttons.
  user_id: string;
  receipt_id: string;
  email?: string | null;
  phoneNumber?: string | null;
}

export const ArticleSection = ({
  index,
  article,
  lang,
  receipt,
  openPopup,
  extended_view_items,
  extended_view_images,
  user_id: _user_id,
  receipt_id: _receipt_id,
  email: _email,
  phoneNumber: _phoneNumber,
}: ArticleSectionProps) => {
  const [expanded, setExpanded] = useState(false);

  const { visibleWidgets } = useExtendedWidgetVisibility({
    article,
    extended_view_items,
    extended_view_images,
    currentView: "article_view",
  });

  const hasExpandableContent =
    article.art_description ?? article.art_number ?? article.reason_for_return;

  const recyclingFee =
    article.additions?.art_recycling_fee ??
    (article.additions?.quantity_recycling_fee != null && article.quantity != null
      ? article.additions.quantity_recycling_fee * article.quantity
      : null);

  const hasBarcode = article.barcodes && article.barcodes.length > 0;
  const articleType = article.type ?? "unknown";

  return (
    <div className="article">
      <div className="split">
        <div>
          {/* Title with art_name */}
          <ExpandableButton
            onClick={() => setExpanded(!expanded)}
            isExpanded={expanded}
            hasExpandableContent={Boolean(hasExpandableContent)}
          >
            {getArticleDisplay(article, lang)}
            <span className={styles.quantityInfo}>
              {article.quantity_final_price != null && (
                <span className={`small ${styles.mutedText}`}>
                  {formatCurrency(
                    article.quantity_final_price,
                    receipt.total.merchant_currency,
                    false,
                    Boolean(article.return)
                  )}
                </span>
              )}
              {article.return && (
                <span className="tag tag--returned">
                  {translate(lang, "Returnert", "Returned")}
                </span>
              )}
            </span>
          </ExpandableButton>

          {/* Always show art_description if exists */}
          {article.art_description && (
            <p className={`small ${styles.descriptionText}`}>
              {article.art_description}
            </p>
          )}

          {/* Add price ex VAT when expanded */}
          {expanded && article.art_price_without_vat != null && (
            <p className={`small ${styles.priceExVat}`}>
              {translate(lang, "Pris eks MVA: ", "Price ex VAT: ")}
              {formatCurrency(
                article.art_price_without_vat,
                receipt.total.merchant_currency,
                true
              )}
            </p>
          )}

          <div className="buttons">
            {/* Ticket button */}
            {hasBarcode && article.type && (
              <Button
                onClick={() => openPopup(articleType, index)}
                text={getArticleButtonText(articleType, lang)}
              />
            )}

            {/* Article-specific extended view service buttons */}
            {visibleWidgets.map((service) => {
              const itemKey = `article_${service.originalIndex}`;
              return (
                <Button
                  key={itemKey}
                  onClick={() =>
                    openPopup(
                      `widget_${service.extended_view_position}`,
                      service.originalIndex,
                      {
                        art_number: article.art_number,
                        art_final_price: article.art_final_price,
                      }
                    )
                  }
                  text={getButtonText(service.extended_view_position, lang, {
                    receipt_button_text: service.receipt_button_text,
                  })}
                  icon={
                    service.imageSrc ? (
                      <Image
                        src={service.imageSrc}
                        alt={service.title}
                        width={16}
                        height={16}
                        className={styles.serviceIcon}
                      />
                    ) : undefined
                  }
                />
              );
            })}
          </div>

          {/* Move recycling fee to bottom of expanded details */}
          <ArticleDetails
            article={article}
            lang={lang}
            receipt={receipt}
            expanded={expanded}
          />
        </div>

        {/* Sum */}
        <div className="text-right">
          {/* Final price */}
          <p className={`amount ${styles.amountNoWrap}`}>
            {formatCurrency(
              article.art_final_price ?? 0,
              receipt.total.merchant_currency,
              false,
              Boolean(article.return)
            )}
          </p>

          {/* Recycling fee - keep in collapsed view */}
          {recyclingFee != null && (
            <p className={`small ${styles.recyclingFee}`}>
              {translate(lang, "Pant: ", "Recycling fee: ")}
              {formatCurrency(recyclingFee, receipt.total.merchant_currency)}
            </p>
          )}

          {/* Discount Groups */}
          {article.discounts?.map((discount, i) => (
            <DiscountLine
              key={`discount-${i}-${discount.description}-${discount.amount}-${discount.percentage}`}
              discount={discount}
              currencyCode={receipt.total.merchant_currency}
            />
          ))}

          {/* VAT Groups */}
          {article.additions?.vat_groups?.map((group, i) => (
            <VatLine
              key={`vat-${i}-${group.percentage}-${group.amount}`}
              group={group}
              currencyCode={receipt.total.merchant_currency}
              lang={lang}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
